import React, { useState } from 'react';
import { supabase } from '../supabase';
import './EquipmentLeaseForm.css';

const EquipmentLeaseForm = () => {
  const [leaseType, setLeaseType] = useState('');
  const [formData, setFormData] = useState({
    // Transaction Identification
    leaseRef: '',
    dateOfApplication: '',
    relatedLoi: '',
    currency: '',
    // Lessee Details
    lesseeName: '',
    lesseeRegNo: '',
    lesseeAddress: '',
    lesseeCountry: '',
    lesseeTin: '',
    lesseeRepresentative: '',
    lesseeEmail: '',
    lesseePhone: '',
    // Lessor Details
    lessorName: '',
    lessorRegNo: '',
    lessorAddress: '',
    lessorCountry: '',
    lessorTin: '',
    lessorRepresentative: '',
    lessorEmail: '',
    lessorPhone: '',
    // Equipment
    equipmentItems: [{ no: 1, equipment: '', manufacturer: '', serialNo: '', hsCode: '', origin: '', specification: '', qty: 1, rentalRate: '', condition: '' }],
    // Commercial Terms
    leaseTerm: '',
    rentalFrequency: '',
    rentalAmount: '',
    securityDeposit: '',
    initialRental: '',
    renewal: '',
    purchaseOption: '',
    latePayment: '',
    // Delivery
    incoterms: '',
    deliveryWindow: '',
    transportMode: '',
    installation: '',
    insurance: '',
    // Use & Maintenance
    usePurpose: '',
    maintenance: '',
    majorRepairs: '',
    // Inspection
    inspectionStage: '',
    acceptanceStandard: '',
    acceptancePeriod: '',
    equipmentCondition: '',
    existingDefects: '',
    // Warranty
    warrantyScope: '',
    serviceResponse: '',
    sparePartsAvailability: '',
    downtimeTreatment: '',
    operatorTraining: '',
    // KYC
    kycVerified: false,
    beneficialOwnership: false,
    amlCompliance: false,
    // ABVIG Role
    abvigRole: '',
    abvigFee: '',
    feeBasis: '',
    // Default
    curePeriod: '',
    earlyTermination: '',
    governingLaw: '',
    disputeResolution: '',
    arbitrationRules: '',
    language: '',
    // Expiry
    expiryDate: '',
    renewalOption: '',
    buyoutPrice: '',
    returnLocation: '',
    finalInspection: '',
    // Signatures
    lesseeNameSigned: '',
    lesseeTitle: '',
    lesseeSignature: '',
    lesseeDate: '',
    abvigName: '',
    abvigTitle: '',
    abvigSignature: '',
    abvigDate: '',
    agreeTerms: false
  });

  const [formStatus, setFormStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleEquipmentChange = (index, field, value) => {
    const items = [...formData.equipmentItems];
    items[index][field] = value;
    setFormData({ ...formData, equipmentItems: items });
  };

  const addEquipmentRow = () => {
    const items = [...formData.equipmentItems];
    items.push({ no: items.length + 1, equipment: '', manufacturer: '', serialNo: '', hsCode: '', origin: '', specification: '', qty: 1, rentalRate: '', condition: '' });
    setFormData({ ...formData, equipmentItems: items });
  };

  const removeEquipmentRow = (index) => {
    if (formData.equipmentItems.length > 1) {
      const items = formData.equipmentItems.filter((_, i) => i !== index);
      items.forEach((item, i) => item.no = i + 1);
      setFormData({ ...formData, equipmentItems: items });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setFormStatus(null);

    if (!formData.lesseeName || !formData.lesseeEmail || !formData.lessorName) {
      setFormStatus('error');
      setLoading(false);
      return;
    }

    try {
      const { error } = await supabase
        .from('equipment_leases')
        .insert([{
          lease_type: leaseType,
          lease_ref: formData.leaseRef,
          lessee_name: formData.lesseeName,
          lessee_email: formData.lesseeEmail,
          lessor_name: formData.lessorName,
          lessor_email: formData.lessorEmail,
          currency: formData.currency,
          lease_term: formData.leaseTerm,
          rental_amount: formData.rentalAmount,
          governing_law: formData.governingLaw,
          status: 'pending'
        }]);

      if (error) throw error;
      setFormStatus('success');
    } catch (error) {
      console.error('Error submitting form:', error);
      setFormStatus('error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="form-page">
      <section className="form-hero">
        <h1>Equipment Leasing & Rental Application</h1>
        <p>For Equipment Lease, Hire and Rental Transactions — Domestic & International</p>
      </section>

      <section className="form-section">
        <div className="container">
          <div className="form-wrapper">
            <div className="form-header">
              <h2>Lease Type — Select ONE</h2>
              <div className="type-selector">
                <button 
                  className={`type-btn ${leaseType === 'operating' ? 'active' : ''}`}
                  onClick={() => setLeaseType('operating')}
                >
                  ☐ Operating Lease / Rental
                </button>
                <button 
                  className={`type-btn ${leaseType === 'finance' ? 'active' : ''}`}
                  onClick={() => setLeaseType('finance')}
                >
                  ☐ Finance Lease
                </button>
                <button 
                  className={`type-btn ${leaseType === 'hire' ? 'active' : ''}`}
                  onClick={() => setLeaseType('hire')}
                >
                  ☐ Hire / Rental
                </button>
              </div>
            </div>

            {formStatus === 'success' && (
              <div className="form-success">
                <h3>✓ Lease Application Submitted!</h3>
                <p>Your equipment lease application has been received. Our team will contact you within 48 hours.</p>
              </div>
            )}

            {formStatus === 'error' && (
              <div className="form-error">
                <h3>⚠️ Submission Error</h3>
                <p>Please fill in all required fields.</p>
              </div>
            )}

            <form onSubmit={handleSubmit}>
              {/* 1. Transaction Identification */}
              <div className="form-section-title">
                <h3>1. TRANSACTION IDENTIFICATION</h3>
              </div>

              <div className="form-row two-col">
                <div className="form-group">
                  <label>Lease / Rental Reference No.</label>
                  <input type="text" name="leaseRef" value={`ABVIG-LEASE-${Date.now().toString().slice(-6)}`} disabled />
                </div>
                <div className="form-group">
                  <label>Date of Application / Order</label>
                  <input type="date" name="dateOfApplication" value={new Date().toISOString().split('T')[0]} disabled />
                </div>
              </div>

              <div className="form-row two-col">
                <div className="form-group">
                  <label>Related LOI / Proposal Reference</label>
                  <input type="text" name="relatedLoi" value={formData.relatedLoi} onChange={handleChange} placeholder="Enter LOI reference" />
                </div>
                <div className="form-group">
                  <label>Currency *</label>
                  <select name="currency" value={formData.currency} onChange={handleChange} required>
                    <option value="">Select currency</option>
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                    <option value="GBP">GBP</option>
                    <option value="NGN">NGN</option>
                  </select>
                </div>
              </div>

              {/* 2. Parties */}
              <div className="form-section-title">
                <h3>2. PARTIES</h3>
              </div>

              <div className="form-subsection">
                <h4>LESSEE / APPLICANT</h4>
                <div className="form-row two-col">
                  <div className="form-group">
                    <label>Legal Name *</label>
                    <input type="text" name="lesseeName" value={formData.lesseeName} onChange={handleChange} required placeholder="Enter lessee name" />
                  </div>
                  <div className="form-group">
                    <label>Registration / Incorporation No.</label>
                    <input type="text" name="lesseeRegNo" value={formData.lesseeRegNo} onChange={handleChange} placeholder="Enter registration number" />
                  </div>
                </div>
                <div className="form-group">
                  <label>Address</label>
                  <input type="text" name="lesseeAddress" value={formData.lesseeAddress} onChange={handleChange} placeholder="Enter address" />
                </div>
                <div className="form-row two-col">
                  <div className="form-group">
                    <label>Country / TIN</label>
                    <input type="text" name="lesseeCountry" value={formData.lesseeCountry} onChange={handleChange} placeholder="Country" />
                  </div>
                  <div className="form-group">
                    <label>Authorised Representative / Title</label>
                    <input type="text" name="lesseeRepresentative" value={formData.lesseeRepresentative} onChange={handleChange} placeholder="Name / Title" />
                  </div>
                </div>
                <div className="form-row two-col">
                  <div className="form-group">
                    <label>Email *</label>
                    <input type="email" name="lesseeEmail" value={formData.lesseeEmail} onChange={handleChange} required placeholder="email@company.com" />
                  </div>
                  <div className="form-group">
                    <label>Telephone</label>
                    <input type="tel" name="lesseePhone" value={formData.lesseePhone} onChange={handleChange} placeholder="+234 800 000 0000" />
                  </div>
                </div>
              </div>

              <div className="form-subsection">
                <h4>LESSOR / EQUIPMENT OWNER</h4>
                <div className="form-row two-col">
                  <div className="form-group">
                    <label>Legal Name *</label>
                    <input type="text" name="lessorName" value={formData.lessorName} onChange={handleChange} required placeholder="Enter lessor name" />
                  </div>
                  <div className="form-group">
                    <label>Registration / Incorporation No.</label>
                    <input type="text" name="lessorRegNo" value={formData.lessorRegNo} onChange={handleChange} placeholder="Enter registration number" />
                  </div>
                </div>
                <div className="form-group">
                  <label>Address</label>
                  <input type="text" name="lessorAddress" value={formData.lessorAddress} onChange={handleChange} placeholder="Enter address" />
                </div>
                <div className="form-row two-col">
                  <div className="form-group">
                    <label>Country / TIN</label>
                    <input type="text" name="lessorCountry" value={formData.lessorCountry} onChange={handleChange} placeholder="Country" />
                  </div>
                  <div className="form-group">
                    <label>Authorised Representative / Title</label>
                    <input type="text" name="lessorRepresentative" value={formData.lessorRepresentative} onChange={handleChange} placeholder="Name / Title" />
                  </div>
                </div>
                <div className="form-row two-col">
                  <div className="form-group">
                    <label>Email *</label>
                    <input type="email" name="lessorEmail" value={formData.lessorEmail} onChange={handleChange} required placeholder="email@company.com" />
                  </div>
                  <div className="form-group">
                    <label>Telephone</label>
                    <input type="tel" name="lessorPhone" value={formData.lessorPhone} onChange={handleChange} placeholder="+234 800 000 0000" />
                  </div>
                </div>
              </div>

              {/* 3. Equipment Schedule */}
              <div className="form-section-title">
                <h3>3. EQUIPMENT LEASE / RENTAL SCHEDULE</h3>
              </div>

              <div className="equipment-table">
                <div className="equipment-header">
                  <span className="col-no">#</span>
                  <span className="col-equipment">Equipment / Model</span>
                  <span className="col-manufacturer">Manufacturer</span>
                  <span className="col-serial">Serial No.</span>
                  <span className="col-qty">Qty</span>
                  <span className="col-rate">Rental Rate</span>
                  <span className="col-condition">Condition</span>
                  <span className="col-action"></span>
                </div>
                {formData.equipmentItems.map((item, index) => (
                  <div className="equipment-row" key={index}>
                    <span className="col-no">{item.no}</span>
                    <input className="col-equipment" type="text" value={item.equipment} onChange={(e) => handleEquipmentChange(index, 'equipment', e.target.value)} placeholder="Equipment name" />
                    <input className="col-manufacturer" type="text" value={item.manufacturer} onChange={(e) => handleEquipmentChange(index, 'manufacturer', e.target.value)} placeholder="Manufacturer" />
                    <input className="col-serial" type="text" value={item.serialNo} onChange={(e) => handleEquipmentChange(index, 'serialNo', e.target.value)} placeholder="Serial No." />
                    <input className="col-qty" type="number" value={item.qty} onChange={(e) => handleEquipmentChange(index, 'qty', parseInt(e.target.value) || 1)} min="1" />
                    <input className="col-rate" type="number" value={item.rentalRate} onChange={(e) => handleEquipmentChange(index, 'rentalRate', e.target.value)} placeholder="0.00" />
                    <input className="col-condition" type="text" value={item.condition} onChange={(e) => handleEquipmentChange(index, 'condition', e.target.value)} placeholder="New/Used" />
                    <button className="col-action" type="button" onClick={() => removeEquipmentRow(index)}>✕</button>
                  </div>
                ))}
                <button type="button" className="add-row-btn" onClick={addEquipmentRow}>+ Add Equipment</button>
              </div>

              {/* 4. Commercial Terms */}
              <div className="form-section-title">
                <h3>4. LEASE / RENTAL COMMERCIAL TERMS</h3>
              </div>

              <div className="form-row two-col">
                <div className="form-group">
                  <label>Lease / Rental Term *</label>
                  <input type="text" name="leaseTerm" value={formData.leaseTerm} onChange={handleChange} required placeholder="e.g., 12 months, 3 years" />
                </div>
                <div className="form-group">
                  <label>Rental Frequency</label>
                  <select name="rentalFrequency" value={formData.rentalFrequency} onChange={handleChange}>
                    <option value="">Select frequency</option>
                    <option value="Monthly">Monthly</option>
                    <option value="Quarterly">Quarterly</option>
                    <option value="Semi-annual">Semi-annual</option>
                    <option value="Annual">Annual</option>
                  </select>
                </div>
              </div>

              <div className="form-row two-col">
                <div className="form-group">
                  <label>Rental Amount (per period) *</label>
                  <input type="number" name="rentalAmount" value={formData.rentalAmount} onChange={handleChange} required placeholder="0.00" />
                </div>
                <div className="form-group">
                  <label>Security Deposit</label>
                  <input type="number" name="securityDeposit" value={formData.securityDeposit} onChange={handleChange} placeholder="0.00" />
                </div>
              </div>

              <div className="form-row two-col">
                <div className="form-group">
                  <label>Initial / Advance Rental</label>
                  <input type="number" name="initialRental" value={formData.initialRental} onChange={handleChange} placeholder="0.00" />
                </div>
                <div className="form-group">
                  <label>Renewal</label>
                  <select name="renewal" value={formData.renewal} onChange={handleChange}>
                    <option value="">Select renewal option</option>
                    <option value="Automatic subject to terms">Automatic subject to terms</option>
                    <option value="By written agreement">By written agreement</option>
                    <option value="Not available">Not available</option>
                  </select>
                </div>
              </div>

              <div className="form-row two-col">
                <div className="form-group">
                  <label>Purchase / Buy-out Option</label>
                  <select name="purchaseOption" value={formData.purchaseOption} onChange={handleChange}>
                    <option value="">Select option</option>
                    <option value="None">None</option>
                    <option value="Available">Available</option>
                    <option value="Mandatory">Mandatory</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Late Payment</label>
                  <input type="text" name="latePayment" value={formData.latePayment} onChange={handleChange} placeholder="e.g., 5% per month" />
                </div>
              </div>

              {/* 5. Use & Maintenance */}
              <div className="form-section-title">
                <h3>6. USE, CUSTODY & MAINTENANCE CONDITIONS</h3>
              </div>

              <div className="form-row two-col">
                <div className="form-group">
                  <label>Routine Maintenance</label>
                  <select name="maintenance" value={formData.maintenance} onChange={handleChange}>
                    <option value="">Select responsibility</option>
                    <option value="Lessor">Lessor</option>
                    <option value="Lessee">Lessee</option>
                    <option value="Shared">Shared</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Major Repairs / Component Replacement</label>
                  <select name="majorRepairs" value={formData.majorRepairs} onChange={handleChange}>
                    <option value="">Select responsibility</option>
                    <option value="Lessor">Lessor</option>
                    <option value="Lessee">Lessee</option>
                    <option value="As separately agreed">As separately agreed</option>
                  </select>
                </div>
              </div>

              {/* 14. Governing Law */}
              <div className="form-section-title">
                <h3>14. GOVERNING LAW & DISPUTE RESOLUTION</h3>
              </div>

              <div className="form-row two-col">
                <div className="form-group">
                  <label>Governing Law</label>
                  <input type="text" name="governingLaw" value={formData.governingLaw} onChange={handleChange} placeholder="e.g., Nigerian Law" />
                </div>
                <div className="form-group">
                  <label>Dispute Resolution</label>
                  <select name="disputeResolution" value={formData.disputeResolution} onChange={handleChange}>
                    <option value="">Select option</option>
                    <option value="Courts">Courts</option>
                    <option value="Arbitration">Arbitration</option>
                    <option value="Mediation then Arbitration">Mediation then Arbitration</option>
                  </select>
                </div>
              </div>

              {/* Signatures */}
              <div className="form-section-title">
                <h3>16. ACKNOWLEDGEMENT & SIGNATURES</h3>
              </div>

              <div className="form-subsection">
                <h4>LESSEE / APPLICANT</h4>
                <div className="form-row three-col">
                  <div className="form-group">
                    <label>Name *</label>
                    <input type="text" name="lesseeNameSigned" value={formData.lesseeNameSigned} onChange={handleChange} required placeholder="Full name" />
                  </div>
                  <div className="form-group">
                    <label>Title</label>
                    <input type="text" name="lesseeTitle" value={formData.lesseeTitle} onChange={handleChange} placeholder="Job title" />
                  </div>
                  <div className="form-group">
                    <label>Date *</label>
                    <input type="date" name="lesseeDate" value={formData.lesseeDate} onChange={handleChange} required />
                  </div>
                </div>
              </div>

              <div className="form-subsection">
                <h4>ABVIG / TRANSACTION COORDINATOR</h4>
                <div className="form-row three-col">
                  <div className="form-group">
                    <label>Name</label>
                    <input type="text" name="abvigName" value={formData.abvigName} onChange={handleChange} placeholder="ABVIG Representative" />
                  </div>
                  <div className="form-group">
                    <label>Title</label>
                    <input type="text" name="abvigTitle" value={formData.abvigTitle} onChange={handleChange} placeholder="Title" />
                  </div>
                  <div className="form-group">
                    <label>Date</label>
                    <input type="date" name="abvigDate" value={formData.abvigDate} onChange={handleChange} />
                  </div>
                </div>
              </div>

              {/* Terms */}
              <div className="form-group checkbox">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="agreeTerms"
                    checked={formData.agreeTerms}
                    onChange={handleChange}
                    required
                  />
                  I confirm that all information provided is accurate and agree to the terms and conditions.
                </label>
              </div>

              <button type="submit" className="btn-gold submit-btn" disabled={loading}>
                {loading ? 'Submitting...' : 'Submit Lease Application'}
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default EquipmentLeaseForm;